import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import {
  KAILAC_PHONE_DISPLAY,
  KAILAC_TEL_HREF,
} from '@/config/contactPhone';

export default function PrivacyPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Privacy Policy | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Kailac Privacy Policy — how we collect, use, and protect your personal and business information. Last updated 2024.');
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="mb-10">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">{t('legal_eyebrow')}</p>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">{t('legal_privacy_title')}</h1>
          <p className="text-sm text-gray-400">{t('legal_last_updated')}: March 1, 2026</p>
        </div>

        <div className="prose max-w-none text-gray-600 leading-relaxed space-y-8">
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p>Kailac (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our advisory services. Please read this policy carefully. If you disagree with its terms, please discontinue use of our services.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
            <p className="mb-3">We may collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Personal Identification Information:</strong> Name, email address, phone number, and mailing address.</li>
              <li><strong>Business Information:</strong> Business name, industry, years in operation, revenue information, and business structure.</li>
              <li><strong>Financial Profile Information:</strong> Self-reported credit information, funding goals, and financial history provided during the application process.</li>
              <li><strong>Usage Data:</strong> IP address, browser type, pages visited, time spent on pages, and other analytics data.</li>
              <li><strong>Communications:</strong> Messages and correspondence you send to us.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide and improve our advisory services</li>
              <li>Communicate with you about your application and services</li>
              <li>Send informational content and updates you have opted into</li>
              <li>Analyze usage patterns to improve our website and services</li>
              <li>Comply with applicable laws and regulations</li>
              <li>Protect against fraudulent or illegal activity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. Information Sharing and Disclosure</h2>
            <p>We do not sell your personal information to third parties. We may share information with:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Service providers who assist in our operations (under confidentiality agreements)</li>
              <li>Legal authorities when required by law or legal process</li>
              <li>Business partners as part of delivering our advisory services, with your consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Data Security</h2>
            <p>We implement industry-standard security measures to protect your personal information. However, no method of internet transmission or electronic storage is 100% secure. We cannot guarantee absolute security of your data.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Your Rights (Including CCPA)</h2>
            <p className="mb-3">Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Right to Know:</strong> Request information about what personal data we collect and how it is used.</li>
              <li><strong>Right to Access:</strong> Request a copy of the personal information we hold about you.</li>
              <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information.</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information, subject to certain exceptions.</li>
              <li><strong>Right to Opt-Out:</strong> California residents have the right to opt out of the sale of personal information. We do not sell personal information.</li>
              <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, contact us at: <a href="mailto:privacy@kailac.com" className="text-[#4F2D63] hover:underline cursor-pointer">privacy@kailac.com</a></p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. California Privacy Rights (CCPA)</h2>
            <p>If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with specific rights. We do not sell your personal information to third parties. If you believe we have information about you that you would like to access, correct, or delete, please contact us. We will respond to verifiable consumer requests within 45 days as required by law.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Cookies</h2>
            <p>Our website uses cookies and similar tracking technologies to analyze usage and improve our service. You can control cookies through your browser settings. By continuing to use our site, you consent to our use of cookies as described in this policy.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. Contact Us</h2>
            <p>For privacy-related questions or to exercise your rights, contact us at: <a href="mailto:privacy@kailac.com" className="text-[#4F2D63] hover:underline cursor-pointer">privacy@kailac.com</a> or call <a href={KAILAC_TEL_HREF} className="text-[#4F2D63] hover:underline cursor-pointer">{KAILAC_PHONE_DISPLAY}</a>.</p>
          </section>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/terms-of-service" className="text-sm text-[#4F2D63] font-semibold hover:underline cursor-pointer">{t('legal_nav_terms')}</Link>
          <Link to="/disclaimer" className="text-sm text-[#4F2D63] font-semibold hover:underline cursor-pointer">{t('legal_nav_disclaimer')}</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
